// @flow
import { injectIntl, intlShape } from 'react-intl';
import React, { Component } from 'react';
import CardCta from './CardCta';
import classNames from 'classnames';
import ConsoleSocial from '../../components/ConsoleSocial/ConsoleSocial';
import DetailsRow from './DetailsRow';
import { getItemUrl } from '../../utils/urls';
import ImageRow from './ImageRow';
import isEqual from 'react-fast-compare';
import ItemSaveContainer from '../../components/ItemSave/ItemSaveContainer';
import noop from '../../utils/noop';
import PriceRow from './PriceRow';
import PromotedBadge from '../../components/Badges/PromotedBadge';
import styles from './ItemCard.module.scss';
import TitleRow from './TitleRow';

type Props = {
    accountInGoodStanding: boolean,
    allowBidding: boolean,
    bidCount: number,
    bidders: number,
    bidLimitBalance: number,
    cardViewModel: any,
    className: string,
    comingUp: boolean,
    currentAskPrice: number,
    currentItemIndex: number,
    hasVideo: boolean,
    hideLiveButton: boolean,
    hideLotNumbers: boolean,
    hideSeller: boolean,
    intl: intlShape,
    isLotOpen: boolean,
    key: number,
    last: boolean,
    leadingBidder: boolean,
    noWidthLimit: boolean,
    openAbsenteeBidModal: Function,
    openOnBlank: boolean,
    openRegisterForAuctionModal: Function,
    openRetractBidModal: Function,
    openSendSellerMessageModal: Function,
    showBanner: boolean,
    showBidStatus: boolean,
    showCta: boolean,
    showDate: boolean,
    showEstimate: boolean,
    showImageWithHover: boolean,
    showLive: boolean,
    showSocial: boolean,
    skipVisibilityCheck: boolean,
    style: any,
    submitLiveBid: Function,
    submitted: boolean,
    trackClick: Function,
};

class ItemCard extends Component<Props> {
    static defaultProps = {
        allowBidding: false,
        bidCount: 0,
        cardViewModel: {
            biddingInfo: {},
            buyNow: {},
            catalog: {
                catalogId: 0,
                currency: 'usd',
            },
            catalogRegistration: {},
            item: {
                salePrice: 0,
                title: '',
            },
            liveItemStatus: {},
            promoted: false,
            seller: {},
        },
        className: '',
        currentItemIndex: 0,
        hideLiveButton: false,
        hideLotNumbers: false,
        hideSeller: false,
        last: false,
        noWidthLimit: false,
        openAbsenteeBidModal: noop,
        openRetractBidModal: noop,
        openSendSellerMessageModal: noop,
        showBanner: false,
        showBidStatus: false,
        showCta: false,
        showDate: true,
        showEstimate: false,
        showImageWithHover: false,
        showSocial: false,
        style: {},
        trackClick: noop,
    };

    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    showSendSellerMessageModal = () => {
        const {
            cardViewModel: {
                catalog,
                seller: { sellerId, name: sellerName },
            },
            openSendSellerMessageModal,
            intl,
        } = this.props;

        const { formatMessage } = intl;

        const messageDetails = {};
        const subject = `${formatMessage({
            id: 'registration_declined',
        })}: ${catalog.title}`;

        openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject);
    };

    render() {
        const {
            accountInGoodStanding,
            allowBidding,
            bidCount,
            bidders,
            bidLimitBalance,
            cardViewModel: {
                biddingInfo,
                buyNow,
                catalog,
                catalogRegistration,
                item,
                liveItemStatus,
                promoted,
                seller,
            },
            className,
            comingUp,
            currentAskPrice,
            currentItemIndex,
            hasVideo,
            hideLiveButton,
            hideLotNumbers,
            hideSeller,
            key,
            last,
            leadingBidder,
            noWidthLimit,
            openAbsenteeBidModal,
            openOnBlank,
            openRegisterForAuctionModal,
            openRetractBidModal,
            showBanner,
            showBidStatus,
            showCta,
            showDate,
            showEstimate,
            showImageWithHover,
            showSocial,
            skipVisibilityCheck,
            style,
            submitted,
            submitLiveBid,
            trackClick,
        } = this.props;

        const {
            bidActivityCount,
            bidderHasBid,
            bidderHasHighBid,
            bidderMaxBid,
            isAvailable,
            isLocked,
            isPassed,
            isReserveMet,
            isSold,
            leadingBid,
            salePrice = 0,
        } = biddingInfo;

        const {
            approved,
            bidLimit,
            blockedByAuctioneer,
            declined,
            limited,
            pending,
            registered,
            suspended,
        } = catalogRegistration;

        const { open: isLotOpen } = liveItemStatus;
        const { price: buyNowPrice, status: buyNowStatus } = buyNow;
        const { catalogId, catalogStatus, currency = 'usd', isCatalogOnly, saleStartTs, title: catalogTitle } = catalog;
        const {
            highBidEstimate = 0,
            imageVersion,
            index,
            isSEOExcluded,
            itemId,
            lotNumber,
            lowBidEstimate = 0,
            startPrice,
            photo,
            photos,
            sellerId,
            title,
        } = item;

        const { name: sellerName } = seller;

        const classes = classNames(styles['item-card'], className, {
            [styles.placeholder]: !item.title,
            [styles['last-card']]: last,
            [styles.showSocial]: showSocial,
            [styles.open]: isLotOpen && !noWidthLimit,
        });

        const itemUrl = getItemUrl(itemId, title);
        const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
        const socialClasses = classNames(styles.social, { [styles.moreMargin]: !noWidthLimit });
        const itemSaveClasses = classNames(styles['item-save'], { [styles.morePadding]: !noWidthLimit });

        return (
            <div key={key} className={classes} onClick={trackClick} style={style}>
                {promoted && <PromotedBadge className={!noWidthLimit ? styles.promoted : ''} />}
                <ItemSaveContainer className={itemSaveClasses} itemId={itemId} />
                <ImageRow
                    accountInGoodStanding={accountInGoodStanding}
                    bidderHasBid={bidderHasBid}
                    bidderHasHighBid={bidderHasHighBid}
                    blockedByAuctioneer={blockedByAuctioneer}
                    catalogId={catalogId}
                    imageVersion={imageVersion}
                    isAvailable={isAvailable}
                    isPassed={isPassed}
                    isReserveMet={isReserveMet}
                    isSEOExcluded={isSEOExcluded}
                    itemId={itemId}
                    noWidthLimit={noWidthLimit}
                    openOnBlank={openOnBlank}
                    pending={pending}
                    photo={photo}
                    photos={photos}
                    sellerId={sellerId}
                    showBidStatus={showBidStatus}
                    showImageWithHover={showImageWithHover}
                    skipVisibilityCheck={skipVisibilityCheck}
                    suspended={suspended}
                    title={title}
                />
                {showSocial && isLotOpen && (
                    <ConsoleSocial bidders={bidders} className={socialClasses} isOnCatalogCard hasVideo={hasVideo} />
                )}
                <TitleRow
                    hideLotNumbers={hideLotNumbers}
                    isSEOExcluded={isSEOExcluded}
                    itemId={itemId}
                    lotNumber={lotNumber}
                    openOnBlank={openOnBlank}
                    title={title}
                />
                <DetailsRow
                    allowBidding={allowBidding}
                    buyNowStatus={buyNowStatus}
                    catalogId={catalogId}
                    catalogStatus={catalogStatus}
                    comingUp={comingUp && isAvailable && !isUnsold}
                    currency={currency}
                    currentItemIndex={currentItemIndex}
                    hideSeller={hideSeller}
                    hideLiveButton={hideLiveButton}
                    highBidEstimate={highBidEstimate}
                    index={index}
                    isCatalogOnly={isCatalogOnly}
                    lowBidEstimate={lowBidEstimate}
                    saleStartTs={saleStartTs}
                    sellerId={sellerId}
                    sellerName={sellerName}
                    showDate={showDate}
                    showEstimate={showEstimate}
                />
                <PriceRow
                    bidActivityCount={bidActivityCount}
                    bidCount={bidCount}
                    buyNowPrice={buyNowPrice}
                    buyNowStatus={buyNowStatus}
                    catalogStatus={catalogStatus}
                    currency={currency}
                    isAvailable={isAvailable}
                    isCatalogOnly={isCatalogOnly}
                    isLotOpen={isLotOpen}
                    isPassed={isPassed}
                    isSold={isSold}
                    isUnsold={isUnsold}
                    leadingBid={leadingBid}
                    salePrice={salePrice}
                    startPrice={startPrice}
                />
                <CardCta
                    accountInGoodStanding={accountInGoodStanding}
                    allowBidding={allowBidding}
                    approved={approved}
                    bidderHasHighBid={bidderHasHighBid}
                    bidderMaxBid={bidderMaxBid}
                    bidLimit={bidLimit}
                    bidLimitBalance={bidLimitBalance}
                    blockedByAuctioneer={blockedByAuctioneer}
                    catalogId={catalogId}
                    catalogStatus={catalogStatus}
                    catalogTitle={catalogTitle}
                    comingUp={comingUp}
                    currency={currency}
                    currentAskPrice={currentAskPrice}
                    declined={declined}
                    isAvailable={isAvailable}
                    isCatalogOnly={isCatalogOnly}
                    isLotOpen={isLotOpen}
                    itemId={itemId}
                    itemUrl={itemUrl}
                    leadingBid={leadingBid}
                    leadingBidder={leadingBidder}
                    limited={limited}
                    openAbsenteeBidModal={openAbsenteeBidModal}
                    openRegisterForAuctionModal={openRegisterForAuctionModal}
                    openRetractBidModal={openRetractBidModal}
                    pending={pending}
                    registered={registered}
                    salePrice={salePrice}
                    saleStartTs={saleStartTs}
                    sellerId={sellerId}
                    showBanner={showBanner}
                    showCta={showCta}
                    showSendSellerMessageModal={this.showSendSellerMessageModal}
                    startPrice={startPrice}
                    submitLiveBid={submitLiveBid}
                    submitted={submitted}
                    suspended={suspended}
                />
            </div>
        );
    }
}

export default injectIntl(ItemCard);
